import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ShieldCheck, LayoutDashboard, Users, Banknote, AlertTriangle, ScrollText } from 'lucide-react';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
  { to: '/admin/users', label: 'Users', icon: Users, exact: false },
  { to: '/admin/withdrawals', label: 'Payouts', icon: Banknote, exact: false },
  { to: '/admin/disputes', label: 'Disputes', icon: AlertTriangle, exact: false },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText, exact: false },
];

// Wraps every admin page: gates non-admins (backend also enforces), shows the
// section tabs, and keeps a consistent header.
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (!isLoading && !user?.is_admin) return <Navigate to="/" replace />;

  const isActive = (to: string, exact: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-6 w-6 text-emerald-400" />
          <h1 className="text-xl font-bold text-white">Admin</h1>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1 touch-pan-x">
          {TABS.map((t) => {
            const active = isActive(t.to, t.exact);
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition ${
                  active ? 'bg-emerald-600 text-white' : 'bg-white/10 text-green-200 hover:bg-white/20'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </div>

        {children}
      </div>
    </Layout>
  );
};

export default AdminLayout;
